import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  BugReport as BugReportIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material'
import { debugAPI, systemAPI } from '../services/api'

function DebugConsole() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const [logs, setLogs] = useState([])
  const [apiLogs, setApiLogs] = useState([])
  const [systemStatus, setSystemStatus] = useState({})
  const [performanceMetrics, setPerformanceMetrics] = useState({})
  const [testDialog, setTestDialog] = useState(false)
  const [testEndpoint, setTestEndpoint] = useState('')
  const [testMethod, setTestMethod] = useState('GET')
  const [testData, setTestData] = useState('')
  const [testResult, setTestResult] = useState(null)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const logContainerRef = useRef(null)

  useEffect(() => {
    loadDebugData()
  }, [])

  useEffect(() => {
    let interval
    if (autoRefresh) {
      interval = setInterval(loadDebugData, 5000) // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [autoRefresh])

  const loadDebugData = async () => {
    try {
      setLoading(true)
      setError('')

      const [
        systemStatusResponse,
        errorLogsResponse,
        apiLogsResponse,
        performanceResponse
      ] = await Promise.allSettled([
        debugAPI.getSystemStatus(),
        debugAPI.getErrorLogs(50),
        debugAPI.getApiLogs(50),
        debugAPI.getPerformanceMetrics()
      ])

      if (systemStatusResponse.status === 'fulfilled' && systemStatusResponse.value.success) {
        setSystemStatus(systemStatusResponse.value.data)
      }

      if (errorLogsResponse.status === 'fulfilled' && errorLogsResponse.value.success) {
        setLogs(errorLogsResponse.value.data)
      }

      if (apiLogsResponse.status === 'fulfilled' && apiLogsResponse.value.success) {
        setApiLogs(apiLogsResponse.value.data)
      }

      if (performanceResponse.status === 'fulfilled' && performanceResponse.value.success) {
        setPerformanceMetrics(performanceResponse.value.data)
      }

    } catch (error) {
      console.error('Error loading debug data:', error)
      setError('Failed to load debug data')
    } finally {
      setLoading(false)
    }
  }

  const handleClearLogs = async (type = 'all') => {
    try {
      setError('')
      setSuccess('')
      
      const response = await systemAPI.clearLogs(type)
      if (response.success) {
        setSuccess(`${type} logs cleared successfully`)
        await loadDebugData()
      } else {
        setError('Failed to clear logs')
      }
    } catch (error) {
      console.error('Error clearing logs:', error)
      setError('Failed to clear logs')
    }
  }

  const handleExportLogs = async (type = 'all', format = 'json') => {
    try {
      const blob = await debugAPI.exportLogs(type, format)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `logs_${type}_${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error exporting logs:', error)
      setError('Failed to export logs')
    }
  }

  const handleTestEndpoint = async () => {
    try {
      setError('')
      setSuccess('')
      
      let data = null
      if (testData.trim()) {
        try {
          data = JSON.parse(testData)
        } catch (e) {
          setError('Invalid JSON data')
          return
        }
      }

      const response = await debugAPI.testEndpoint(testEndpoint, testMethod, data)
      setTestResult(response)
      setSuccess('Endpoint test completed')
    } catch (error) {
      console.error('Error testing endpoint:', error)
      setTestResult({
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      })
      setError('Endpoint test failed')
    }
  }

  const getLogIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />
      case 'warning':
        return <WarningIcon color="warning" fontSize="small" />
      case 'info':
        return <InfoIcon color="info" fontSize="small" />
      case 'success':
        return <CheckCircleIcon color="success" fontSize="small" />
      default:
        return <BugReportIcon color="action" fontSize="small" />
    }
  }

  const LogEntry = ({ log, index }) => (
    <Box
      key={index}
      className={`debug-log-entry ${log.level?.toLowerCase() || 'info'}`}
      sx={{ mb: 1, p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        {getLogIcon(log.level)}
        <Typography variant="caption" sx={{ ml: 1, fontWeight: 'bold' }}>
          {new Date(log.timestamp).toLocaleString()}
        </Typography>
        <Chip
          label={log.level || 'INFO'}
          size="small"
          sx={{ ml: 1, height: 20 }}
          color={
            log.level === 'error' ? 'error' :
            log.level === 'warning' ? 'warning' :
            log.level === 'success' ? 'success' : 'default'
          }
        />
      </Box>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {log.message}
      </Typography>
      {log.context && (
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          Context: {JSON.stringify(log.context, null, 2)}
        </Typography>
      )}
    </Box>
  )

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )

  const StatusCard = ({ title, value, status, description }) => (
    <Card className="hover-card">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">{title}</Typography>
          <Chip
            label={status}
            color={status === 'healthy' || status === 'online' ? 'success' : 'error'}
            size="small"
          />
        </Box>
        <Typography variant="h4" sx={{ mb: 1 }}>
          {value}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Debug Console</Typography>
        <Box>
          <Button
            variant={autoRefresh ? 'contained' : 'outlined'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            sx={{ mr: 2 }}
          >
            Auto Refresh {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDebugData}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => setTestDialog(true)}
          >
            Test Endpoint
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* System Status Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatusCard
            title="System Status"
            value={systemStatus.status || 'Unknown'}
            status={systemStatus.status || 'unknown'}
            description="Overall system health"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatusCard
            title="Memory Usage"
            value={`${(systemStatus.memory_usage || 0).toFixed(1)}%`}
            status={systemStatus.memory_usage > 80 ? 'critical' : 'healthy'}
            description="Current memory consumption"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatusCard
            title="Response Time"
            value={`${performanceMetrics.avg_response_time || 0}ms`}
            status={performanceMetrics.avg_response_time > 1000 ? 'slow' : 'healthy'}
            description="Average API response time"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatusCard
            title="Error Rate"
            value={`${((systemStatus.error_count || 0) / (systemStatus.total_requests || 1) * 100).toFixed(1)}%`}
            status={systemStatus.error_count > 10 ? 'critical' : 'healthy'}
            description="Error rate in last hour"
          />
        </Grid>
      </Grid>

      {/* Debug Tabs */}
      <Paper>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`Error Logs (${logs.length})`} />
          <Tab label={`API Logs (${apiLogs.length})`} />
          <Tab label="Performance" />
          <Tab label="System Info" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {/* Error Logs */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Error Logs</Typography>
            <Box>
              <Tooltip title="Export Logs">
                <IconButton onClick={() => handleExportLogs('error', 'json')}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear Logs">
                <IconButton onClick={() => handleClearLogs('error')} color="error">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Paper
            ref={logContainerRef}
            className="debug-console"
            sx={{ maxHeight: 500, overflow: 'auto', p: 2 }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : logs.length > 0 ? (
              logs.map((log, index) => <LogEntry key={index} log={log} index={index} />)
            ) : (
              <Typography color="text.secondary">No error logs found</Typography>
            )}
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* API Logs */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">API Request Logs</Typography>
            <Box>
              <Tooltip title="Export Logs">
                <IconButton onClick={() => handleExportLogs('api', 'json')}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear Logs">
                <IconButton onClick={() => handleClearLogs('api')} color="error">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Paper className="debug-console" sx={{ maxHeight: 500, overflow: 'auto', p: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : apiLogs.length > 0 ? (
              apiLogs.map((log, index) => <LogEntry key={index} log={log} index={index} />)
            ) : (
              <Typography color="text.secondary">No API logs found</Typography>
            )}
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {/* Performance Metrics */}
          <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Response Times
                  </Typography>
                  <Typography variant="body2">
                    Average: {performanceMetrics.avg_response_time || 0}ms
                  </Typography>
                  <Typography variant="body2">
                    Min: {performanceMetrics.min_response_time || 0}ms
                  </Typography>
                  <Typography variant="body2">
                    Max: {performanceMetrics.max_response_time || 0}ms
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Request Statistics
                  </Typography>
                  <Typography variant="body2">
                    Total Requests: {performanceMetrics.total_requests || 0}
                  </Typography>
                  <Typography variant="body2">
                    Successful: {performanceMetrics.successful_requests || 0}
                  </Typography>
                  <Typography variant="body2">
                    Failed: {performanceMetrics.failed_requests || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {/* System Information */}
          <Typography variant="h6" gutterBottom>System Information</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Server Info
                  </Typography>
                  <Typography variant="body2">
                    PHP Version: {systemStatus.php_version || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    Server: {systemStatus.server_software || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    Uptime: {Math.floor((systemStatus.uptime || 0) / 3600)}h
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Database Info
                  </Typography>
                  <Typography variant="body2">
                    Status: {systemStatus.database_status || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    Version: {systemStatus.database_version || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    Connections: {systemStatus.database_connections || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Test Endpoint Dialog */}
      <Dialog open={testDialog} onClose={() => setTestDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Test API Endpoint</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Endpoint URL"
              value={testEndpoint}
              onChange={(e) => setTestEndpoint(e.target.value)}
              placeholder="/admin_api.php/system/health"
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Method</InputLabel>
              <Select
                value={testMethod}
                onChange={(e) => setTestMethod(e.target.value)}
                label="Method"
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
            {(testMethod === 'POST' || testMethod === 'PUT') && (
              <TextField
                fullWidth
                label="Request Data (JSON)"
                value={testData}
                onChange={(e) => setTestData(e.target.value)}
                multiline
                rows={4}
                placeholder='{"key": "value"}'
                margin="normal"
              />
            )}
            {testResult && (
              <Paper sx={{ p: 2, mt: 2, backgroundColor: 'grey.100' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Test Result:
                </Typography>
                <pre style={{ fontSize: '0.875rem', overflow: 'auto' }}>
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialog(false)}>Close</Button>
          <Button
            onClick={handleTestEndpoint}
            variant="contained"
            disabled={!testEndpoint}
          >
            Test Endpoint
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DebugConsole